import React from "react";
import { getRequest } from "sharedUtils/httpUtils";
import MUIDataTable from "mui-datatables";
import { convertToNumericMonthAndDayAndYearAndTime } from "sharedUtils/timeUtils";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import { CheckIconBlack } from "../../icons";
import PenIcon from "components/icons/PenIcon";

const { DateTime } = require("luxon");

class AwinMerchantData extends React.Component {
    constructor(props) {
        super(props);
        this.tableRef = React.createRef();

        this.state = {
            merchants: [],
            merchantsLoading: false,
            MerchantsTable: {
                title: "Affiliate merchants",
                options: {
                    selectableRows: "multiple",
                    rowsPerPage: 100,
                    sortOrder: {
                        name: 'merchantName',
                        direction: 'asc'
                    },
                },
                getTheme: () => createTheme({
                    overrides: {
                        MUIDataTableBodyRow: {
                            root: {
                                '&:nth-child(odd)': {
                                    backgroundColor: '#F9F9F9'
                                },
                                cursor: 'pointer'
                            }
                        },
                    }
                })
            },
        };
    }

    componentDidMount = () => {
        window.scrollTo({ left: 0, top: 0, behavior: 'auto' });
        this.getMerchants();
    };

    getMerchants = async () => {
        let url = `/api/Merchant/GetAwinMerchants`;

        this.setState({ merchantsLoading: true });

        try {
            const merchants = await getRequest(url);

            this.setState({
                merchants,
                merchantsLoading: false,
            });
        } catch (error) {
            console.error("Error fetching merchants:", error);
            this.setState({ merchants: [], merchantsLoading: false });
        }
    };

    onTabChange = (tab) => {
        this.setState({ tab });
    }

    render() {
        const columns = [
            {
                name: "hostname",
                label: "Hostname",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <span>
                                {value}
                            </span>
                        );
                    }
                }
            },
            {
                name: "displayUrl",
                label: "Display URL",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <a href={value} target="_blank" rel="noopener noreferrer">
                                {value}
                            </a>
                        );
                    }
                }
            },
            {
                name: "logoUrl",
                label: "Logo URL",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <img
                                src={value}
                                alt="Merchant Logo"
                                style={{ maxHeight: "50px", maxWidth: "100px" }}
                            />
                        );
                    }
                }
            },
            {
                name: "clickThroughUrl",
                label: "Click Through URL",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <a href={value} target="_blank" rel="noopener noreferrer">
                                {value}
                            </a>
                        );
                    }
                }
            },
            {
                name: "merchantId",
                label: "Merchant ID",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <span>
                                {value}
                            </span>
                        );
                    }
                }
            },
            {
                name: "name",
                label: "Merchant Name",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <b>
                                {value}
                            </b>
                        );
                    }
                }
            },
            {
                name: "currencyCode",
                label: "Currency Code",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <span>
                                {value}
                            </span>
                        );
                    }
                }
            },
            {
                name: "deeplinkEnabled",
                label: "Deeplink Enabled",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <p style={{ textAlign: "center", margin: 0 }}>
                                {value === "true" ? "✅ Yes" : "❌ No"}
                            </p>
                        );
                    }
                }
            },
            {
                name: "description",
                label: "Description",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <span>
                                {value ?? "-"}
                            </span>
                        );
                    }
                }
            },
            {
                name: "membershipStatus",
                label: "Membership Status",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <p style={{ textAlign: "center", margin: 0 }}>
                                {value === "Joined" ? "🟢 Joined" : "🔴 Inactive"}
                            </p>
                        );
                    }
                }
            },
            {
                name: "primaryRegion",
                label: "Primary Region",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <span>
                                {value}
                            </span>
                        );
                    }
                }
            },
            {
                name: "kpi",
                label: "KPI",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <div style={{ width: "200px", wordBreak: "break-word" }}>
                                {value}
                            </div>
                        );
                    }
                }
            },
            {
                name: "commissionRange",
                label: "Commission Range",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        return (
                            <span>
                                {value}
                            </span>
                        );
                    }
                }
            },
            {
                name: "validDomains",
                label: "Valid Domains",
                options: {
                    filter: true,
                    sort: true,
                    customBodyRender: (value) => {
                        if (!value) return "-";

                        let domainList;

                        try {
                            const parsedValue = typeof value === 'string'
                                ? JSON.parse(value)
                                : value;

                            if (!Array.isArray(parsedValue) || parsedValue.length === 0) {
                                return "-";
                            }

                            domainList = parsedValue
                                .map(domainObj => domainObj.domain)
                                .filter(Boolean); // Ensures only valid domains are included

                            return domainList.length > 1
                                ? domainList.join(", ")
                                : domainList[0] ?? "-";

                        } catch (error) {
                            console.error("Invalid JSON for validDomains:", value);
                            return "-";
                        }
                    }
                }
            }

        ];


        return (
            <div>
                <ThemeProvider theme={this.state.MerchantsTable.getTheme()}>
                    <MUIDataTable
                        title={
                            <div style={{ fontFamily: "'Archivo', sans-serif" }}>
                                <p style={{ fontSize: '22px', marginTop: "20px" }}>{this.props.affiliateProgramManager?.name + " merchants" ?? "No affiliate selected"}</p>
                            </div>
                        }
                        data={this.state.merchants}
                        columns={columns}
                        options={{
                            ...this.state.MerchantsTable.options,
                            textLabels: {
                                body: { noMatch: this.state.merchantsLoading ? "LOADING..." : "No data available" },
                            },
                            selectableRows: "none",
                        }}
                        ref={this.tableRef}
                    />
                </ThemeProvider>
            </div>
        );
    }
}

export default AwinMerchantData;
